import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { Fragment } from 'react';
import styled from 'styled-components';

import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { absCenter } from '@sorare/core/src/lib/style';

import type { So5RankingRewards_so5Lineup } from './__generated__/index.graphql';

const Separator = styled.div`
  width: 1px;
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;

const FlameContainer = styled(Horizontal).attrs({ center: true })`
  position: relative;
`;

const CenteredNumber = styled(BrandXXS)`
  text-shadow:
    0 -3px 4.5px rgba(0, 0, 0, 0.6),
    0 0 4.5px var(--c-limited-tertiary);

  ${absCenter}
`;

type Props = { so5Lineup: So5RankingRewards_so5Lineup };

export const So5RankingRewards = ({ so5Lineup }: Props) => {
  const {
    flags: { useStreakInLive = false },
  } = useFeatureFlags();

  const so5Ranking = so5Lineup?.so5Rankings.at(0);

  if (!so5Ranking) {
    return null;
  }

  const { eligibleOrSo5Rewards } = so5Ranking;

  const rewardConfigs = eligibleOrSo5Rewards.flatMap(
    so5RewardOrConfig => so5RewardOrConfig.rewardConfigs
  );

  const streakReward =
    useStreakInLive &&
    so5Lineup?.myThresholdsStreakTask?.completedBy?.id === so5Lineup?.id &&
    so5Lineup?.myThresholdsStreakTask?.rewardConfigs.at(0);

  return (
    <Horizontal gap={1.5}>
      {rewardConfigs.map((rewardConfig, i) => (
        <Fragment key={rewardConfig.id}>
          <AnyRewardConfig
            anyRewardConfig={rewardConfig}
            variant={Variant.TEXT}
          />
          {i < rewardConfigs.length - 1 && <Separator />}
        </Fragment>
      ))}
      {!!streakReward && !!so5Lineup?.myThresholdsStreakTask && (
        <>
          {rewardConfigs.length > 0 && <Separator />}
          <Horizontal gap={0.5}>
            <AnyRewardConfig
              anyRewardConfig={streakReward}
              variant={Variant.TEXT}
              customText={({ rewardText }) => (
                <span
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color':
                        'linear-gradient(323deg, #FEF314 -3.1%, #FF4E22 52.7%)',
                    } as CSSProperties
                  }
                >
                  {rewardText}
                </span>
              )}
            />
            <FlameContainer>
              <Flame size={2.5} />
              <CenteredNumber>
                {so5Lineup.myThresholdsStreakTask.progress + 1}
              </CenteredNumber>
            </FlameContainer>
          </Horizontal>
        </>
      )}
    </Horizontal>
  );
};

So5RankingRewards.fragments = {
  so5Lineup: gql`
    fragment So5RankingRewards_so5Lineup on So5Lineup {
      id
      myThresholdsStreakTask {
        id
        completedBy {
          id
        }
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
        progress
      }
      so5Rankings {
        id
        eligibleOrSo5Rewards {
          ... on So5RewardConfig {
            id
            score
            ranks
            rankPct
            rewardConfigs {
              id
              ...AnyRewardConfig_anyRewardConfigInterface
            }
          }
          ... on So5Reward {
            slug
            rewardConfigs(multiplyBaseRewards: true) {
              id
              ...AnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<So5RankingRewards_so5Lineup>,
};
